import { create } from 'zustand';
import { votesApi } from '../api/votes';
import type { Vote } from '../types';

interface VoteState {
  // Votes by subset ID
  votes: Map<number, Vote>;
  isLoading: boolean;
  error: string | null;

  // Actions
  loadVote: (subsetId: number) => Promise<void>;
  saveDraft: (subsetId: number, rankedSongIds: number[], specialCategoryVotes?: (number | null)[]) => Promise<void>;
  submitVote: (subsetId: number, rankedSongIds: number[], specialCategoryVotes?: (number | null)[]) => Promise<void>;
  clearError: () => void;
}

export const useVoteStore = create<VoteState>((set, get) => ({
  votes: new Map(),
  isLoading: false,
  error: null,

  loadVote: async (subsetId: number) => {
    set({ isLoading: true, error: null });
    try {
      const response = await votesApi.getUserVote(subsetId);
      if (response.hasVoted && response.vote) {
        const votes = new Map(get().votes);
        votes.set(subsetId, response.vote);
        set({ votes, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to load vote';
      set({ error: errorMessage, isLoading: false });
    }
  },

  saveDraft: async (subsetId: number, rankedSongIds: number[], specialCategoryVotes?: (number | null)[]) => {
    // Save draft without showing loading state (silent background save)
    try {
      await votesApi.saveDraft(subsetId, rankedSongIds, specialCategoryVotes);
      // Reload the vote to get updated state
      await get().loadVote(subsetId);
    } catch (error: unknown) {
      // Silent fail for drafts - don't interrupt user workflow
      console.error('Failed to save draft:', error);
    }
  },

  submitVote: async (subsetId: number, rankedSongIds: number[], specialCategoryVotes?: (number | null)[]) => {
    set({ isLoading: true, error: null });
    try {
      await votesApi.submitVote(subsetId, rankedSongIds, specialCategoryVotes, true);
      // Reload the vote to get updated timestamp
      await get().loadVote(subsetId);
    } catch (error: unknown) {
      const errorMessage = (error as { message?: string })?.message || 'Failed to submit vote';
      set({ error: errorMessage, isLoading: false });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
